import type { CreateElement, PropType, VNode, ShallowReactive } from "vue";
import { defineComponent, shallowReactive } from "vue";

export type ControlIn = {
  visible: boolean;
  enabled: boolean;
  readOnly: boolean;
};

export type IControl = {
  parent: IControl | null;
  in: ControlIn;
  render(h: CreateElement): VNode;
  resolveEnabled(): boolean;
  resolveReadOnly(): boolean;
};

export abstract class Control implements IControl {
  #in: ShallowReactive<ControlIn>;
  parent: IControl | null = null;

  abstract render(h: CreateElement): VNode;

  constructor() {
    this.#in = shallowReactive({
      visible: true,
      enabled: true,
      readOnly: false,
    });
  }

  get in() {
    return this.#in;
  }

  resolveEnabled() {
    return (this.parent?.resolveEnabled() ?? true) && this.in.enabled;
  }

  resolveReadOnly() {
    return (this.parent?.resolveReadOnly() ?? false) || this.in.readOnly;
  }
}

export class EmptyControl extends Control {
  render(h: CreateElement): VNode {
    return h("span", "");
  }

  constructor() {
    super();
  }
}

export const WrappedControl = defineComponent({
  name: "WrappedControl",
  props: {
    control: {
      type: Object as PropType<IControl>,
      required: true,
    },
  },
  setup(props) {
    return (h: CreateElement) =>
      props.control.in.visible ? props.control.render(h) : undefined;
  },
});

export function extendIn<TIn1, TIn2>(
  in1: TIn1,
  in2: TIn2
): ShallowReactive<TIn1 & TIn2> {
  return shallowReactive(Object.assign({}, in1, in2)) as ShallowReactive<
    TIn1 & TIn2
  >;
}

export function mergeIn<TCtrl extends { in: any }>(ctrl: TCtrl, opts?: {}) {
  if (opts) {
    for (const key in opts) {
      if (Object.hasOwn(ctrl.in, key)) {
        (ctrl.in as any)[key] = (opts as any)[key];
      }
    }
  }

  return ctrl;
}

export function mergePartial<T>(a?: Partial<T>, b?: Partial<T>) {
  return Object.assign({}, a ?? {}, b ?? {}) as T;
}

export function tsAssign<T1 extends {}, T2 extends {}>(a: T1, b: T2) {
  return Object.assign({}, a, b) as T1 & T2;
}
